<template>
  <div class="currency-details">
    <div
      :style="{
        'background-image': `url(${backgroundImage})`
      }"
      class="currency-details__bg" />
    <div class="currency-details__body">
      <h2 class="currency-details__title">
        {{ currencyOfferFieldValues.primary }} - {{ offer.amount }} {{ !currencySettings.title.includes('Gold') ?
          currencySettings.title.toLowerCase() : currencySettings.title }} in stock
      </h2>

      <div class="currency-details__info">
        <div
          v-if="currencyOfferFieldValues"
          class="currency-details__sticker-list">
          <div
            v-for="field in currencyOfferFieldValues.regular"
            :key="field"
            class="currency-details__sticker">
            {{ field }}
          </div>
        </div>

        <div class="currency-details__delivery-wrapper">
          <p>
            Guaranteed delivery
          </p>

          <div class="currency-details__delivery">
            <div class="currency-details__delivery-icon" />
            <text-date
              :value="+offer.guarantee_delivery_time"
              :is-editable="false" />
          </div>
        </div>
      </div>

      <div class="currency-details__wrapper-outer">
        <div class="currency-details__foreground">
          <img
            :data-src="foregroundImage"
            alt=""
            class="currency-details__foreground-img lazyload">
        </div>

        <div class="currency-details__wrapper">
          <div class="currency-details__gold-wrapper">
            <div class="currency-details__gold-inner">
              <div class="currency-details__gold-counter">
                <ui-counter
                  v-model.number="goldAmount"
                  :min="offer.min_purchase_amount"
                  :max="offer.amount"
                  :step="currencySettings.step_size"
                  class="currency-details__gold-input" />
              </div>

              <p class="currency-details__gold-detail">
                Minimum amount — {{ offer.min_purchase_amount }} {{ currencySettings.title }}
              </p>
            </div>
            <div class="currency-details__cost">
              <upper-cents-price :sum="price.purePrice || 0" />
            </div>
          </div>

          <cashback-switcher
            :price="price.purePrice"
            type="gold"
            class="currency-details__cashback-switcher"
            @cashback-applied="handleCashbackApplied" />

          <div
            v-if="currentExpressDetails && currentExpressDetails.is_active"
            class="currency-details__checkbox-container">
            <ui-checkbox
              v-model="isExpressOrder"
              class="currency-details__checkbox">
              <div class="currency-details__checkbox-value">
                Express delivery

                <div class="currency-details__checkbox-price">
                  {{ activeRegion.id === RegionModel.USA.id ? '$' : '€' }}{{ currentExpressPrice }}
                </div>
              </div>
            </ui-checkbox>
          </div>

          <cashback-badge
            :price="price.purePrice"
            type="product-gold"
            class="currency-details__cashback">
            cashback after purchase
            <info-tooltip text="Will be added to your Skycoach balance" />
          </cashback-badge>

          <currency-seller
            :user="offer.user"
            class="currency-details__seller"
            :class="{'currency-details__seller--pointer': !data.shouldDisableSellerLink }"
            @click.native="goToSeller" />

          <ui-input
            v-model.trim="characterName"
            :error="errors.characterName"
            error-mode="background"
            placeholder="Character name"
            :class="{ 'currency-details__input-wide': user || unavailableText }"
            class="currency-details__input"
            :z-index-tooltip="2147483011"
            @input="errors.characterName = ''" />

          <ui-input
            v-if="!user && !unavailableText"
            v-model.trim="email"
            :error="errors.email"
            error-mode="background"
            name="email"
            inputmode="email"
            autocomplete="off"
            class="currency-details__input"
            placeholder="Email"
            :z-index-tooltip="2147483011"
            @input="errors.email = ''" />

          <div class="currency-details__payment-systems">
            <img
              v-for="item in paymentMethods"
              :key="item.id"
              class="payment-item"
              height="14"
              width="37"
              alt=""
              :src="`/images/payment_systems/white/${item.pic}`">
          </div>

          <div class="currency-details__buttons">
            <div
              v-if="data.stripeData.availableStripePaymentMethods.googlePay"
              class="button currency-details__buy-gpay transition--color-bg"
              @click.stop="buyWithStripe">
              <template v-if="loading">
                Please wait...
              </template>

              <template v-else>
                <img
                  alt=""
                  width="51"
                  height="21"
                  src="/images/payment_systems/google-pay.svg"
                  class="gpay-button__img">
              </template>
            </div>

            <button
              v-if="userIsBooster"
              class="button button--disabled currency-details__buy-now">
              Pro can't buy
            </button>

            <button
              v-else-if="unavailableText"
              class="button button--disabled currency-details__buy-now">
              {{ unavailableText }}
            </button>

            <div
              v-else
              class="button button-main currency-details__buy-now"
              @click.stop="openPaymentMethodSelector">
              <img
                data-src="/images/icons/shield.svg"
                class="lazyload currency-details__buy-now--img"
                width="14"
                height="14"
                alt="">
              Buy Now
            </div>
          </div>

          <div class="currency-details__bottom-separator">
            <p class="separator-text">
              or
            </p>
          </div>

          <div class="currency-details__chat">
            <div class="chat__info">
              <p class="any-questions">
                Any questions?
              </p>

              <p class="before-order">
                Chat before ordering
              </p>
            </div>

            <div
              class="chat__button button button-additional"
              @click="openIntercom">
              <img
                data-src="/images/icons/chat2.svg"
                width="24"
                height="24"
                alt=""
                class="lazyload chat__button-img">
              <p class="chat__button-text">
                {{ isMobile ? "Chat" : "Chat with Support" }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import AppModel from 'Models/AppModel';
import GameModel from 'Models/GameModel';
import UserModel from 'Models/UserModel';
import RegionModel from 'Models/RegionModel';
import PaymentSystemModel from 'Models/PaymentSystemModel';
import yandexMetrika from 'Root/drivers/yandexMetrika';
import criteoAnalytics from 'Root/drivers/criteoAnalytics';
import IntercomApiDriver from 'Root/drivers/IntercomApiDriver';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import { getAdvertisingParamsFromSession } from 'Root/helpers/handleAdvertisingParams';
import { regExpEmail, regExpExcludeDigitsSpecials, urlRegexp } from 'Root/helpers/regexps';
import TextDate from 'Components/TextDate.vue';
import UiInput from 'Components/ui/Input/UiInput.vue';
import CashbackBadge from 'Components/CashbackBadge.vue';
import CashbackSwitcher from 'Components/CashbackSwitcher.vue';
import UiCounter from 'Components/ui/Input/UiCounter.vue';
import CurrencySeller from 'Components/Currency/CurrencySeller.vue';
import UpperCentsPrice from 'Components/ui/Price/UpperCentsPrice.vue';
import PaymentMethodSelector from 'Components/PaymentPopups/PaymentMethodSelector.vue';
import { getPriceWithCommission, openSellerPage } from 'Root/helpers/product';
import UiCheckbox from 'Components/ui/Checkbox/UiCheckbox.vue';
import { addBuyLogs } from 'Root/helpers/tools';
import InfoTooltip from 'Components/ui/Tooltip/InfoTooltip.vue';

const DEFAULT_ERROR_MESSAGE = 'An error has occurred. Try again.';

export default {
  name: 'GoldDetailsPopup',

  components: {
    UiCheckbox,
    TextDate,
    CurrencySeller,
    CashbackBadge,
    CashbackSwitcher,
    UpperCentsPrice,
    UiInput,
    UiCounter,
    InfoTooltip,
  },

  props: {
    data: {
      /** PROPS:
       * product
       * server
       * stripeData
       * isSplitView
       * shouldDisableSellerLink
       * */
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      email: '',
      characterName: '',
      goldAmount: 0,
      selectedPaymentMethod: null,
      errors: {
        characterName: '',
        email: '',
        general: '',
      },
      usedCashback: {
        value: 0,
        isApplied: false,
      },
      isGoldProduct: true,
      paypalPrice: null,

      isExpressOrder: false,
      RegionModel,
    };
  },

  watch: {
    selectedPaymentMethod(val) {
      if (val === PaymentSystemModel.METHODS.PAYPAL.id || val === PaymentSystemModel.METHODS.EPOCH.id) {
        this.paypalPrice = this.price.newPrice;
      }
    },
  },

  computed: {
    ...mapState(['user', 'activeRegion', 'userDevice', 'usdToCurrencyRatio']),
    ...mapGetters(['calcPriceFromUsd', 'isUsersFirstOrder']),

    currencySettings() {
      return this.product.product_currency_setting || this.product || null;
    },

    offer() {
      return this.data.offer;
    },

    product() {
      return this.data.product;
    },

    server() {
      return this.data.server;
    },

    purePrice() {
      const usdPrice = this.offer.price_per_step * (this.goldAmount / this.currencySettings.step_size);
      return this.calcPriceFromUsd(this.activeRegion.id, usdPrice, false);
    },

    price() {
      const price = {
        purePrice: this.purePrice,
      };
      if (this.usedCashback.isApplied && this.usedCashback.value) {
        price.purePrice -= this.usedCashback.value;
      }

      if (this.isExpressOrder && this.currentExpressDetails?.is_active) {
        price.purePrice += this.currentExpressPrice;
      }

      price.newPrice = getPriceWithCommission(price.purePrice, this.selectedPaymentMethod, {
        regionId: this.activeRegion.id,
        isFirstPurchase: this.isUsersFirstOrder,
      });

      return price;
    },

    currentExpressPrice() {
      let expressPrice = 0;
      if (this.currentExpressDetails.type === 1) {
        // Additive absolute value
        expressPrice = this.currentExpressDetails.value;
      } else if (this.currentExpressDetails.type === 2) {
        // Multiplicative value
        expressPrice = this.purePrice * (this.currentExpressDetails.value / 100);
      }

      return Math.round(expressPrice * 100) / 100;
    },

    currencyOfferFieldValues() {
      const fields = {
        primary: null,
        regular: [],
      };

      this.currencySettings.product_currency_setting_fields.forEach((field) => {
        if (typeof this.offer.data[field.id] === 'undefined') {
          return;
        }

        if (field.is_primary) {
          fields.primary = this.offer.data[field.id];
        } else {
          fields.regular.push(this.offer.data[field.id]);
        }
      });

      return fields;
    },

    paymentMethods() {
      return PaymentSystemModel.METHODS_LIST.filter(
        (method, index, list) => list.findIndex((item) => item.title === method.title) === index,
      );
    },

    userIsBooster() {
      return [UserModel.ROLES.PRO.id, UserModel.ROLES.PRO_CANDIDATE.id].includes(+this.user?.role);
    },

    unavailableText() {
      return this.product.meta?.unavailableText;
    },

    foregroundImage() {
      const imagePath = this.product.picture_item_small || this.product.picture_item;

      if (imagePath) {
        return `/storage${imagePath}`;
      }

      return '/images/games/picture_item.png';
    },

    backgroundImage() {
      return this.product.picture_bg ? `/storage${this.product.picture_bg}` : '/images/temp/card-bg.jpg';
    },

    isStripeButtonVisible() {
      const stripeAvailable = this.data.stripeData.availableStripePaymentMethods.googlePay;
      const userCanUseStripe = PaymentSystemModel.METHODS.GOOGLE_PAY.onlyForUsersWithId
        ? PaymentSystemModel.METHODS.GOOGLE_PAY.onlyForUsersWithId.includes(this.user?.id)
        : true;
      return stripeAvailable && userCanUseStripe;
    },

    userEmail() {
      return this.user?.email || this.email;
    },

    isMobile() {
      return this.userDevice.innerWidth < 640;
    },

    currentExpressDetails() {
      return this.currencySettings?.express?.[this.activeRegion.regionCode.toLowerCase()];
    },
  },

  created() {
    this.goldAmount = this.offer.min_purchase_amount;
  },

  methods: {
    openIntercom() {
      googleTagAnalytics.emitItemOpenChatEvent();
      IntercomApiDriver.show();
    },

    validateEmail() {
      if (!this.email.match(regExpEmail)) {
        this.errors.email = 'Please, enter a valid email';
        googleTagAnalytics.fireError('buy_now_1', 'wrong_email');
        return false;
      }

      return true;
    },

    validateCharacterName() {
      if (!this.characterName || !this.characterName.match(regExpExcludeDigitsSpecials)) {
        this.errors.characterName = 'Please, enter valid character name';
        googleTagAnalytics.fireError('buy_now_1', 'empty_character');
        return false;
      }

      return true;
    },

    sendGooglePaymentAnalytics() {
      const game = GameModel.ID_TO_DATA[this.product.game].fullTitle;
      const currency = RegionModel.ID_TO_DATA[this.$store.state.activeRegion.id].currency.title;
      const runs = 1;
      googleTagAnalytics.emitOpenPaymentNew(game, this.product, this.price.newPrice, currency, runs);

      const tagId = this.product?.master_tag_id || this.product.tags[0].id;
      const orderTag = this.product.tags.find((tag) => tag.id === tagId);
      googleTagAnalytics.firePaymentPopup(this.product, orderTag.title, this.price.newPrice);

      criteoAnalytics.fireAddToCartEvent(this.user?.email || this.email, this.product?.id, this.price?.newPrice, runs);
    },

    openPaymentMethodSelector() {
      const isCharacterNameValid = this.validateCharacterName();
      const isEmailValid = this.user || (!this.user && this.validateEmail());

      if (!isCharacterNameValid || !isEmailValid) {
        return;
      }

      this.$vuedals.openMobilePanel({
        class: this.selfplayOption && !this.selfplayOption.isChecked ? 'vuedals__payment-selector' : null,
        title: 'Choose payment method',
        component: PaymentMethodSelector,
        data: {
          component: this,
        },
        onClose: ({ methodId, wmNumber }) => {
          this.order(this.userEmail, methodId, wmNumber);
        },
        onDismiss: () => {
          this.selectedPaymentMethod = null;
        },
      });

      this.sendGooglePaymentAnalytics();
    },

    disableInterface() {
      this.loading = true;
      this.$store.dispatch('showScreenHolder', 'payment');
    },

    enableInterface() {
      this.loading = false;
      this.$store.dispatch('hideScreenHolder');
    },

    async order(email, paymentMethodId, wmNumber, shouldRedirect) {
      if (this.loading) {
        return;
      }

      this.errors = {
        characterName: '',
        email: '',
        general: '',
      };
      this.disableInterface();

      // попап выбора платёжки внутри попапа голды, поэтому 2 вызова
      this.$vuedals.dismiss();
      this.$vuedals.dismiss();

      try {
        // eslint-disable-next-line consistent-return
        return this.createOrder(paymentMethodId, wmNumber, shouldRedirect, email);
      } catch (e) {
        this.enableInterface();
        googleTagAnalytics.emitBuyItem(false);
        this.errors.general = DEFAULT_ERROR_MESSAGE;
      }
    },

    handleCashbackApplied(cashback) {
      this.usedCashback = {
        ...cashback,
        isApplied: cashback.isApplied,
      };
    },

    createOrderData(paymentMethodId, wmNumber, email) {
      const orderData = {
        runs: 1,
        payment_system: paymentMethodId,
        region_id: this.activeRegion.id,
        product_currency_amount: this.goldAmount,
        product_currency_offer_id: this.offer.id,
        is_express: this.isExpressOrder,
      };

      const meta = {};

      meta.connectionInfo = this.$store.state.connectionInfo;
      meta.advertisingParams = getAdvertisingParamsFromSession();

      if (this.usedCashback?.isApplied && this.usedCashback?.value) {
        meta.cashback = this.usedCashback.value;
      }

      if (paymentMethodId === PaymentSystemModel.CARDPAY_2_WEBMONEY.id) {
        meta.receiver_WMZ = wmNumber;
      }

      // todo SKY-3296 decide postpay fate
      if (!this.user) {
        orderData.email = email;
      } /* else {
        const detailsByCharacterName = this.user.meta?.order_details?.[this.product.game]
          ?.find((detail) => detail.characterName === this.characterName
            && detail.region === this.activeRegion.id
            && detail.server === this.server.id
            && detail.faction === this.faction.id);

        if (detailsByCharacterName) {
          orderData.details = detailsByCharacterName;
        }
      } */
      // todo SKY-3296 remove goldInfo from everywhere except character name
      meta.goldInfo = {
        characterName: this.characterName,
      };

      orderData.meta = JSON.stringify(meta);

      const referee = localStorage.getItem('referee');

      if (referee) {
        orderData.referee = referee;
      }

      return orderData;
    },

    async createOrder(paymentMethodId, wmNumber, shouldRedirect = true, email) {
      const orderData = this.createOrderData(paymentMethodId, wmNumber, email);
      let data;
      await this.$axios
        .post(`${AppModel.API_URI}/product/${this.product.id}/buy`, orderData)
        .then((d) => {
          data = d.data;
        })
        .catch((error) => {
          addBuyLogs(error);
          this.enableInterface();
          this.orderError = error.response.data.message;
        });

      googleTagAnalytics.emitBuyItem(true);

      if (this.data.isSplitView) {
        googleTagAnalytics.fireGtagEvent(paymentMethodId, 'split_marketplace_purchase_new', null, this.price?.newPrice);
        yandexMetrika.invokeEvent('split_marketplace_purchase_new');
      }

      if (!data?.url) {
        return;
      }

      if ('localStorage' in window) {
        localStorage.setItem('activeGameId', this.$store.state.activeGame.id);
      }

      if (shouldRedirect) {
        if (data.url.match(urlRegexp)) {
          document.location.href = data.url;
        } else if (window.stripe) {
          const { error } = await window.stripe.redirectToCheckout({
            sessionId: data.url,
          });

          if (error) {
            this.enableInterface();
            throw new Error(`Payment Error: ${error.message}`);
          }
        } else {
          this.enableInterface();
          throw new Error('window.stripe is undefined');
        }
      }

      // eslint-disable-next-line consistent-return
      return Promise.resolve(data);
    },

    async buyWithStripe() {
      const isCharacterNameValid = this.validateCharacterName();
      const isEmailValid = this.user || (!this.user && this.validateEmail());
      if (!isCharacterNameValid || !isEmailValid) {
        return;
      }

      this.sendGooglePaymentAnalytics();

      const response = await this.order(this.userEmail, PaymentSystemModel.METHODS.GOOGLE_PAY.id, null, false);
      if (response) {
        const [redirectUrl, clientSecret] = response.url && response.url.includes('&client_secret=')
          ? response.url.split('&client_secret=')
          : [response.url, null];
        console.log('order response:', redirectUrl, clientSecret);
        const currency = this.activeRegion.currency.title.toLowerCase();

        this.data.stripeData.stripePaymentRequest.update({
          currency,
          total: {
            label: this.product?.title,
            amount: this.data.stripeData.parsePriceToStripeAmount(this.price.newPrice.toFixed(2)),
          },
        });

        if (clientSecret && redirectUrl) {
          this.listenStripeRequest(redirectUrl, clientSecret);
          this.data.stripeData.stripePaymentRequest.show();
        }
      }
    },

    listenStripeRequest(redirectUrl, clientSecret) {
      this.data.stripeData.stripePaymentRequest.on('paymentmethod', (event) => {
        console.log('stripePaymentRequest.on: PAYMENT METHOD', event);
        // Confirm the PaymentIntent without handling potential next actions (yet).
        this.data.stripeData.stripe
          .confirmCardPayment(clientSecret, { payment_method: event.paymentMethod.id }, { handleActions: false })
          .then((confirmResult) => {
            if (confirmResult.error) {
              // Report to the browser that the payment failed, prompting it to
              // re-show the payment interface, or show an error message and close
              // the payment interface.
              event.complete('fail');
              console.log('FAIL | PAYMENT EVENT COMPLETED');
              this.enableInterface();
            } else {
              // Report to the browser that the confirmation was successful, prompting
              // it to close the browser payment method collection interface.
              event.complete('success');
              console.log('SUCCESS | PAYMENT EVENT COMPLETED');
              // Check if the PaymentIntent requires any actions and if so let Stripe.js
              // handle the flow. If using an API version older than "2019-02-11" instead
              // instead check for: `paymentIntent.status === "requires_source_action"`.
              if (confirmResult.paymentIntent.status === 'requires_action') {
                // Let Stripe.js handle the rest of the payment flow.
                this.data.stripeData.stripe.confirmCardPayment(clientSecret).then((result) => {
                  if (result.error) {
                    alert('Failed to create payment. Please try another payment method.');
                    // The payment failed -- ask your customer for a new payment method.
                  } else {
                    document.location.href = redirectUrl;
                    // The payment has succeeded.
                  }
                });
              } else {
                document.location.href = redirectUrl;
                // The payment has succeeded.
              }
            }
          });
      });

      this.data.stripeData.stripePaymentRequest.on('token', (event) => {
        console.log('stripePaymentRequest.on: TOKEN', event);
      });

      this.data.stripeData.stripePaymentRequest.on('source', (event) => {
        console.log('stripePaymentRequest.on: SOURCE', event);
      });

      this.data.stripeData.stripePaymentRequest.on('cancel', () => {
        console.log('stripePaymentRequest.on: CANCEL');
        this.enableInterface();
      });
    },

    goToSeller() {
      if (this.data?.shouldDisableSellerLink) {
        return;
      }

      // eslint-disable-next-line camelcase
      const { user_id, product_currency_setting_id } = this.offer;

      openSellerPage(user_id, product_currency_setting_id);
    },
  },
};
</script>

<style lang="scss">
.vuedal.currency-details-popup {
  max-width: 800px;
  width: 90vw;

  .mobile-panel-popup {
    background-color: $color--basic-black;
  }

  .mobile-panel-popup__close {
    z-index: 2;
  }
}

@media screen and (min-width: 601px) {
  .vuedal.currency-details-popup {
    .mobile-panel-popup__body {
      padding: 0 48px 48px;
    }
  }
}
</style>

<style lang="scss" scoped>
.currency-details {
  line-height: 1.2;
  font-weight: 500;
}

.currency-details__cashback-switcher {
  display: flex;
  justify-content: space-between;
  align-items: center;
  grid-column:  1 / -1;
}

.currency-details__body {
  position: relative;
}

.currency-details__body,
.currency-details__wrapper {
  display: grid;
  grid-gap: 16px;
}

.currency-details__wrapper-outer {
  margin-top: 8px;
}

.currency-details__delivery-wrapper {
  display: none;
}

.currency-details__title {
  font-size: 17px;
  text-align: center;
}

.currency-details__logo {
  display: none;
}

.currency-details__info {
  display: flex;
  align-items: center;
}

.currency-details__sticker-list {
  --gap: 8px;

  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: calc(-1 * var(--gap));
  margin-right: calc(-1 * var(--gap));
  justify-content: center;
  width: 100%;
}

.currency-details__sticker {
  text-transform: uppercase;
  padding: 6px;
  font-size: 11px;
  background-color: $color--basic-blue;
  border-radius: 4px;
  margin-bottom: var(--gap);
  margin-right: var(--gap);
}

.currency-details__payment-systems {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: -4px;
}

.currency-details__payment-systems,
.currency-details__buttons,
.currency-details__cashback {
  grid-column: 1 / -1;
}

.currency-details__cashback {
  --badge-background-color: #{$color--basic-blue};

  text-align: center;
}

.payment-item {
  filter: $filter--white-to-dark-gray;
  height: 14px;
  width: auto;
  margin: 0 4px 4px;
}

.currency-details__bottom-separator {
  display: flex;
  justify-content: center;
  align-items: center;
}

.separator-text {
  position: relative;
  width: 100%;
  text-align: center;
  color: $color--basic-dark-gray;
  font-size: $text-13px;

  &:after,
  &:before {
    content: "";
    width: 45%;
    border-bottom: solid 1px $color--basic-dark-gray;
    position: absolute;
    top: 50%;
  }

  &:before {
    left: 0;
  }

  &:after {
    right: 0;
  }
}

.currency-details__cost {
  padding-top: 8px;
  font-size: 21px;
  min-width: 3em;
  text-align: right;
}

.currency-details__seller--pointer {
  cursor: pointer;
}

.currency-details__chat {
  display: flex;
  justify-content: space-between;
  text-transform: none;
}

.any-questions {
  color: $color--basic-dark-gray;
  font-weight: 400;
  font-size: $text-11px;
  margin-bottom: 4px;
}

.before-order {
  font-size: $text-13px;
}

.chat__button {
  display: flex;
  align-items: center;
  padding: 5px 14px 7px 9px;
  border-radius: 55px;
  font-size: $text-13px;
  margin-left: 10px;
  text-transform: none;
}

.chat__button-img {
  margin-right: 5px;
}

.currency-details__buy-now {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40px;
  font-size: 13px;
}

.currency-details__buy-now--img {
  margin-right: 9px;
  height: auto;
}

.currency-details__buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  grid-gap: 16px;
}

.currency-details__buy-gpay {
  background-color: $color--basic-menu;
  display: grid;
  place-items: center;
  min-height: 40px;

  &:hover {
    background-color: $color--basic-dark-gray;
  }
}

.currency-details__input {
  --height: 40px !important;
  --font-size: 15px !important;
  --color: #{$color--basic-light-gray};
  --background-color: #{$color--basic-dark-gray};
  --padding: 8px 13px !important;
}

.currency-details__gold-wrapper {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 16px;
}

.currency-details__gold-counter {
  border: 2px solid rgba($color--basic-blue, 0.25);
  background-color: #{rgba($color--basic-blue, 0.25)};
  border-radius: 4px;
  font-size: 15px;
}

.currency-details__gold-input {
  --background-color: transparent;
  --background-color--focused: transparent;
  --font-size: 16px;
  --font-weight: 500 !important;
  --padding: 0 16px !important;
  --height: 44px !important;
  --text-align: center;
  --border-radius: 4px;

  width: 100%;
  border: none;
  background: none;
  color: $color--basic-white;
  font-size: 15px;
  outline: none;
}

.currency-details__foreground-img {
  display: none;
}

.currency-details__gold-detail {
  font-size: 11px;
  margin-top: 8px;
}

.currency-details__input-wide {
  grid-column: 1 / -1 !important;
}

.currency-details__checkbox-container {
  grid-column: 1 / -1;
}

.currency-details__checkbox {
  --size: 20px;
  --check-size: 10px;
  --bg-color: transparent;
  --bg-color-checked: transparent;
  --border: 2px solid #{$color--basic-blue};
  --check-color: #{$color--basic-blue};
  --border-radius: 3px;
}

.currency-details__checkbox-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-weight: 500;
  font-size: 13px;
  line-height: 13px;
}

.currency-details__checkbox-price {
  display: flex;
  align-items: center;
}

.currency-details__checkbox-tooltip {
  margin-left: 8px;
  font-size: 10px;
}

@media screen and #{$screen-less-than-640} {
  .currency-details__gold-wrapper,
  .currency-details__seller {
    margin-bottom: 8px;
  }
}

@media screen and #{$screen-more-or-640} {
  .currency-details__wrapper {
    background-color: $color--basic-black;
    grid-template-columns: 1fr 1fr;
  }

  .currency-details__chat,
  .currency-details__bottom-separator,
  .currency-details__seller,
  .currency-details__gold-wrapper {
    grid-column: 1 / -1;
  }

  .currency-details__title {
    font-size: 25px;
  }

  .currency-details__input {
    --height: 48px !important;
  }

  .currency-details__cost {
    padding-top: 4px;
    font-size: 35px;
  }

  .currency-details__buy-now {
    min-height: 48px;
    font-size: 15px;
  }

  .currency-details__gold-counter {
    margin-right: 0;
  }
}

@media screen and #{$screen-more-or-768} {
  .currency-details__bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
  }

  .payment-item {
    height: 20px;
  }

  .currency-details__wrapper-outer {
    margin-top: 16px;
    display: grid;
    grid-gap: 16px;
    grid-template-columns: minmax(0, 1fr) max-content;
    position: relative;
  }

  .currency-details__wrapper {
    border-radius: 16px;
    padding: 16px;
    max-width: 400px;
  }

  .currency-details__logo {
    display: inline-block;
    margin-right: 16px;
  }

  .currency-details__title {
    grid-column: 1 / -1;
    text-align: left;
    font-size: 35px;
  }

  .currency-details__sticker-list {
    width: auto;
    margin-right: calc(24px - var(--gap));
  }

  .currency-details__info {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }

  .currency-details__delivery {
    display: flex;
    align-items: center;
  }

  .currency-details__delivery-wrapper {
    display: flex;
    align-items: center;
    font-size: 13px;
  }

  .currency-details__delivery {
    margin-left: 8px;
  }

  .currency-details__delivery-icon {
    background: $color--basic-white;
    mask: url(/images/icons/clock.svg) no-repeat center;
    mask-size: cover;
    -webkit-mask: url(/images/icons/clock.svg) no-repeat center;
    -webkit-mask-size: cover;
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  .currency-details__foreground-img {
    display: block;
    margin-top: 32px;
  }

  .currency-details__foreground {
    padding-top: 48px;
  }

  .currency-details__cashback {
    text-align: left;
  }
}

@media screen and #{$screen-more-or-1024} {
  .currency-details {
    min-width: 700px;
  }

  .currency-details__seller {
    position: absolute;
    left: 0;
    top: 0;
  }
}
</style>
